// AssociationDetailDialog.js
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { TimeSelect } from '../../ui/TimeSelect';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';
import { activityAPI, hospitalityAPI, marketingAPI } from '../../../services/api';
import { Pencil, AlertCircle, Calendar, User, Link2, Clock, Sparkles, Trash2, Mail, MessageSquare, CheckCircle2, Upload, Camera, DollarSign, FileText, Gift, Users, Star, Building2, Download, EyeOff } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { associationAPI } from '../../../services/api';
import api from '../../../services/api';
import { useConfirm } from '../../../components/common/ConfirmDialog';

// Normalize legacy relationship values to current enum keys
const RELATIONSHIP_ALIASES = { 'veterinarian': 'vet', 'vet_practice': 'other' };
const normalizeRel = (val) => RELATIONSHIP_ALIASES[val] || val;

export const AssociationDetailDialog = ({ open, onOpenChange, association, entityId, entities = [], onSuccess, canEditAccount = true, usersMap = {} }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();
  const [formData, setFormData] = useState({
    relationship: 'other',
    status: 'current_confirmed',
    notes: '',
    is_key_association: false,
    job_title: '',
  });

  const relationshipTypes = [
    { value: 'shareholder_company', label: 'Shareholder Company' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'employee', label: 'Employee' },
    { value: 'agent', label: 'Agent' },
    { value: 'trainer', label: 'Trainer' },
    { value: 'vet', label: 'Veterinarian' },
    { value: 'boarding', label: 'Boarding' },
    { value: 'family_close', label: 'Family (Close)' },
    { value: 'family_extended', label: 'Family (Extended)' },
    { value: 'other', label: 'Other' },
  ];

  const associationStatuses = [
    { value: 'current_confirmed', label: 'Current (Confirmed)' },
    { value: 'current_possible', label: 'Current (Possible)' },
    { value: 'past_confirmed', label: 'Past (Confirmed)' },
    { value: 'past_possible', label: 'Past (Possible)' },
  ];

  useEffect(() => {
    if (association && open) {
      setFormData({
        relationship: normalizeRel(association.relationship || 'other'),
        status: association.status || 'current_confirmed',
        notes: association.notes || '',
        is_key_association: association.is_key_association || false,
        job_title: association.job_title || '',
      });
      setIsEditing(false);
    }
  }, [association?.id, open]);

  const getEntityName = (id) => {
    const entity = entities.find(e => e.id === id);
    return entity ? entity.system_name : 'Unknown Entity';
  };

  const getStatusLabel = (status) => {
    const statusObj = associationStatuses.find(s => s.value === status);
    return statusObj ? statusObj.label : status;
  };

  const getStatusType = (status) => {
    if (status.includes('current')) return 'Current';
    if (status.includes('past')) return 'Past';
    return status;
  };

  const getStatusConfirmation = (status) => {
    if (status.includes('confirmed')) return 'Confirmed';
    if (status.includes('possible')) return 'Possible';
    return '';
  };

  const handleSave = async () => {
    if (!association?.id) return;
    setSaving(true);
    try {
      await associationAPI.update(association.id, formData);
      toast.success('Association updated');
      setIsEditing(false);
      onSuccess?.();
    } catch (error) {
      console.error('Failed to update association:', error);
      toast.error('Failed to update association');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!association?.id) return;
    if (!await confirm({ title: 'Are you sure you want to delete this association?', destructive: true })) return;
    
    setSaving(true);
    try {
      await associationAPI.delete(association.id);
      toast.success('Association deleted');
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Failed to delete association:', error);
      toast.error('Failed to delete association');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    if (association) {
      setFormData({
        relationship: normalizeRel(association.relationship || 'other'),
        status: association.status || 'current_confirmed',
        notes: association.notes || '',
        is_key_association: association.is_key_association || false,
        job_title: association.job_title || '',
      });
    }
    setIsEditing(false);
  };

  if (!association) return null;

  const otherId = association.crm_entity_1_id === entityId ? association.crm_entity_2_id : association.crm_entity_1_id;
  const otherEntityName = getEntityName(otherId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="association-detail-dialog">
        <DialogHeader>
          <DialogTitle>Association Details</DialogTitle>
        </DialogHeader>

        {isEditing ? (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Associated Entity</Label>
              <div className="p-3 bg-muted/30 rounded flex items-center gap-2">
                <Building2 className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">{otherEntityName}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Relationship *</Label>
                <Select 
                  key={`rel-${association?.id}-${isEditing}`}
                  value={formData.relationship || undefined}
                  onValueChange={(v) => setFormData({...formData, relationship: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select relationship" />
                  </SelectTrigger>
                  <SelectContent>
                    {relationshipTypes.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Status *</Label>
                <Select 
                  key={`status-${association?.id}-${isEditing}`}
                  value={formData.status || undefined}
                  onValueChange={(v) => setFormData({...formData, status: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {associationStatuses.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Job Title / Role Description</Label>
              <Input 
                value={formData.job_title}
                onChange={(e) => setFormData({...formData, job_title: e.target.value})}
                placeholder="e.g. Head Trainer"
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Details about this relationship..."
                rows={3}
              />
            </div>

            <div className="flex items-center space-x-2 pt-2">
              <input
                type="checkbox"
                id="edit-key-assoc"
                checked={formData.is_key_association}
                onChange={(e) => setFormData({...formData, is_key_association: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="edit-key-assoc" className="font-medium flex items-center gap-2 cursor-pointer">
                Key Association <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
              </Label>
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-1">
            <div className="p-4 bg-muted/30 rounded">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-muted-foreground" />
                  <h3 className="text-lg font-semibold">{otherEntityName}</h3>
                  {association.is_key_association && (
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  )}
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Relationship:</span>
                  <Badge variant="outline">
                    {relationshipTypes.find(r => r.value === normalizeRel(association.relationship))?.label || association.relationship}
                  </Badge>
                </div>

                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Status:</span>
                  <Badge variant={association.status.includes('confirmed') ? 'default' : 'secondary'}>
                    {getStatusType(association.status)}
                  </Badge>
                  <Badge variant="outline">
                    {getStatusConfirmation(association.status)}
                  </Badge>
                </div>

                {association.job_title && (
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Job Title:</span>
                    <span className="font-medium">{association.job_title}</span>
                  </div>
                )}

                {association.notes && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-muted-foreground text-xs mb-1">Notes:</p>
                    <p className="text-sm">{association.notes}</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Created Info */}
            {association.created_at && (
              <div className="pt-3 border-t text-xs text-muted-foreground">
                Created: {new Date(association.created_at).toLocaleDateString()} 
                {association.created_by && usersMap[association.created_by] && (
                  <> by {usersMap[association.created_by].name}</>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {isEditing ? (
            <div className="flex justify-between w-full">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={handleDelete}
                disabled={saving}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={cancelEdit} disabled={saving}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-between w-full items-center">
              {canEditAccount && (
                <Button variant="ghost" size="icon" onClick={() => setIsEditing(true)} data-testid="edit-association-btn">
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
              <Button variant="outline" onClick={() => onOpenChange(false)} className="ml-auto">
                Close
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

